import type { MaybeRef } from '@vueuse/core'
import { useStorage } from '@vueuse/core'
import type { Component } from 'vue'
import { PROMPT_TYPES } from '../utils/constants'

import Gemini from '../components/Icon/Gemini/index.vue'
import ChatGPT from '../components/Icon/ChatGPT/index.vue'
import Midjourney from '../components/Icon/Midjourney/index.vue'
import GoogleLM from '../components/Icon/GoogleLM/index.vue'

import type { APIPromptTypeId } from '../types/api'
import { generateTagsHtml } from '../utils/generateTemplate'
import type { Elemental } from '../types/elemental'
import type {
  AvailableTools,
  Prompt,
  PromptMinified,
  PromptShareTemplate,
} from '@/types/prompt'

export const PROMPT_TYPE_STORAGE_KEY = 'filter:prompt-types'
export const PROMPT_TYPE_STORAGE_DEFAULT = [PROMPT_TYPES.Text]

export interface IParsedPrompt {
  raw: string
  formatted: string
  display: string
  variables: string[]
}
interface IParsedPromptWithId extends IParsedPrompt {
  id: string
}

type IStoredPrompts = Record<
  IParsedPromptWithId['id'],
  IParsedPrompt | IParsedPromptWithId
>
export const usePrompts = () => useState<IStoredPrompts>('prompts', () => ({}))

export function useParsePrompt(
  rawPrompt: MaybeRef<string>,
  id?: MaybeRef<string>,
): IParsedPrompt | IParsedPromptWithId {
  const result: IParsedPrompt = {
    raw: unref(rawPrompt).replaceAll('\n\n', '\n'),
    formatted: '',
    display: '',
    variables: [],
  }

  result.formatted = result.raw.replace(/#\[(.*?)\]/g, (_match, p1) => {
    if (!result.variables.includes(p1))
      result.variables.push(p1)

    const color = getVariableColor(result.variables.indexOf(p1))

    const styles = `
      color: ${color[0]};
      background-color: ${color[1]};
      border-radius: 4px;
    `.replace(/\s/g, '')

    return /* html */ `<span style="${styles}" data-tag="${p1}">#${p1}</span>`
  })

  result.display = result.raw.replace(/#\[(.*?)\]/g, (_match, p1) => `#${p1}`)

  if (id) {
    (result as IParsedPromptWithId).id = unref(id)

    const storedPrompts = usePrompts()
    storedPrompts.value[unref(id)] = result
  }

  return result
}

export type UpdatePromptState = 'added' | 'removed'
interface UpdatePrompt {
  state: UpdatePromptState
  id: string
  nVotes?: number
}
// TODO: refactor this to avoid using "watch" in the component
export const useUpdatePromptSave = () =>
  useState<null | UpdatePrompt>('saved-prompt-update', () => null)
export const useUpdatePromptVote = () =>
  useState<null | UpdatePrompt>('voted-prompt-update', () => null)

export const useAccessedPrompts = () =>
  useState<Record<string, Prompt | null>>('accessed-prompts', () => ({}))
export const useAccessedPrompt = () =>
  useState<null | Elemental>('accessed-prompt', () => null)
export const useShareBackgrounds = () => useState<string[]>('share-backgrounds')
export const useShareTemplates = () =>
  useState<PromptShareTemplate[]>('share-templates')

export function getPromptTypeIcon(tool: AvailableTools) {
  const iconComponents: Record<AvailableTools, Component> = {
    Gemini,
    ChatGPT,
    Midjourney,
    'Dall-E': ChatGPT,
    'Google LM': GoogleLM,
  }

  return iconComponents[tool]
}

export function setupDefaultPromptType() {
  const filters = useFilters()
  const extension = useExtension()
  const extensionType = useExtensionType()
  const extensionTypeReady = useExtensionTypeReady()
  const route = useRoute()

  if (route.query.type && !Number.isNaN(route.query.type)) {
    filters.value = {
      ...filters.value,
      type_ids: [Number(route.query.type) as APIPromptTypeId],
    }
  }

  const promptTypeIDS = computed(() => filters.value.type_ids)
  const localExtensionTypes = useStorage(
    PROMPT_TYPE_STORAGE_KEY,
    PROMPT_TYPE_STORAGE_DEFAULT,
  )

  if (localExtensionTypes.value !== filters.value.type_ids)
    localExtensionTypes.value = filters.value.type_ids

  if (extensionTypeReady.value)
    return

  if (!extension.value) {
    syncRefs(promptTypeIDS, localExtensionTypes)
    return
  }

  const typesPerPlatform: Record<AvailableTools, PROMPT_TYPES> = {
    'Gemini': PROMPT_TYPES.Text,
    'ChatGPT': PROMPT_TYPES.Text,
    'Midjourney': PROMPT_TYPES.Visual,
    'Dall-E': PROMPT_TYPES.Visual,
    'Google LM': PROMPT_TYPES.Audio,
  }

  filters.value.type_ids = [typesPerPlatform[extensionType.value]]
  extensionTypeReady.value = true
}

interface FeaturedPromptsState {
  prompts: PromptMinified[]
  usedPrompts: string[]
}
export const useFeaturedPrompts = () =>
  useState<FeaturedPromptsState>(
    'featured-prompts',
    () => ({ prompts: [], usedPrompts: [] } as FeaturedPromptsState),
  )

export function getNextPrompt(
  typeIds: APIPromptTypeId[] = [1],
  topicId?: number,
  skipUsedCheck = false,
): PromptMinified | undefined {
  const featured = useFeaturedPrompts().value
  const canGetNextPrompt
    = skipUsedCheck || featured.usedPrompts.length < featured.prompts.length

  if (!canGetNextPrompt)
    return

  let filteredPrompts = featured.prompts.filter(p =>
    typeIds.includes(p.typeId as APIPromptTypeId),
  )

  if (topicId && topicId !== 0) {
    filteredPrompts = featured.prompts.filter(p =>
      p.topicIds?.includes(topicId),
    )
  }

  let triesCounter = 0
  const getNextIndex = (): number => {
    if (triesCounter >= 18 || !canGetNextPrompt)
      return -1

    triesCounter++
    if (!skipUsedCheck) {
      filteredPrompts = filteredPrompts.filter(
        p => !featured.usedPrompts.includes(p.id),
      )
    }

    const promptIndex = Math.floor(Math.random() * filteredPrompts.length)

    if (
      !filteredPrompts[promptIndex]
      || (!skipUsedCheck
        && featured.usedPrompts.includes(filteredPrompts[promptIndex].id))
    )
      return getNextIndex()

    return promptIndex
  }

  const index = getNextIndex()
  if (index !== -1 && !skipUsedCheck && filteredPrompts[index])
    featured.usedPrompts.push(filteredPrompts[index].id)
  return filteredPrompts[index]
}

export const resetUsedPrompts = () => {
  const featured = useFeaturedPrompts().value
  featured.usedPrompts = []
}

export const useAdsListing = () => useState<number[]>('ads-listing', () => [])

export function getFormattedPrompt(rawPrompt: MaybeRef<string | null>) {
  const templateData = generateTemplate(unref(rawPrompt), {
    tagMarker: '#',
    displayParser: generateTagsHtml,
  })
  if (!templateData)
    return ''
  const variableValues = reactive<Record<string, string>>(
    templateData.variables?.reduce((acc, p) => ({ ...acc, [p]: '' }), {}) ?? {},
  )
  return templateData.transformer(variableValues, generateTagsHtml) ?? ''
}

export function usePromptTemplateParser(
  rawPrompt: Ref<string | null> | ComputedRef<string | null>,
) {
  const templateData = computed(() => {
    if (!rawPrompt.value)
      return null
    return generateTemplate(rawPrompt.value, {
      tagMarker: '#',
      displayParser: generateTagsHtml,
    })
  })

  const variableValues = reactive<Record<string, string>>({})

  // Watch for changes in templateData to update variableValues
  watchEffect(() => {
    if (templateData.value?.variables) {
      const newVariables = templateData.value.variables.reduce(
        (acc, p) => ({ ...acc, [p]: variableValues[p] || '' }),
        {} as Record<string, string>,
      )

      // Clear old variables and set new ones
      Object.keys(variableValues).forEach((key) => {
        if (!(key in newVariables))
          delete variableValues[key]
      })

      Object.assign(variableValues, newVariables)
    }
  })

  const formatted = computed(
    () =>
      templateData.value?.transformer(variableValues, generateTagsHtml) ?? '',
  )

  const getCopyPrompt = (parser?: TagParser) => {
    const textToCopy
      = templateData.value?.transformer(variableValues, parser) ?? ''
    return textToCopy
  }

  return {
    formatted,
    templateData,
    variableValues,
    rawPrompt,
    getCopyPrompt,
  }
}

const ProvideParsePromptKey = Symbol('ProvideParsePrompt') as InjectionKey<
  ReturnType<typeof usePromptTemplateParser>
>

export function ProvideUsePromptTemplate(
  rawPrompt: Ref<string | null> | ComputedRef<string | null>,
) {
  provide(ProvideParsePromptKey, usePromptTemplateParser(rawPrompt))
}

export function usePromptTemplate() {
  return inject(ProvideParsePromptKey)
}
